/**
 * The words no copy about a paid feature may use (codegen note 2.2 §16, ruling
 * R-0916-1; Terms of Use 2.2 §8.7).
 *
 * THE CONSTRAINT, IN COUNSEL'S OWN WORDS:
 *
 * > Banned in relation to paid features: gift, gift card, voucher, credit,
 * > credits, balance, wallet, funds, money, cash, transfer/send/share/give,
 * > bonus, free credits, extra credits. "Reset" is the only word for the
 * > overflow purchase.
 *
 * WHY THIS IS ONE LIST AND NOT SEVEN INLINE ARRAYS. Four screens and a shelf of
 * email templates each had their own idea of what was banned; "bonus" was in
 * two of them and "voucher" in none. A rule with a regulatory consequence
 * cannot be enforced by whichever sentence a test author happened to remember,
 * so the list is here and every assertion imports it.
 *
 * WHAT CHANGED WITH THE CREDIT, AND WHY THE LIST GOT LONGER RATHER THAN
 * SHORTER. Ruling R-0914-1 (14) let "balance", "wallet" and "ledger" through on
 * the ground that they were the product's own nouns — the wallet room, the
 * ledger, the balance a receipt reported. R-0916-1 deleted all three rooms.
 * They are not the product's nouns any more, they are the nouns of a product
 * this one deliberately is not, and a reset that read as a balance would reopen
 * the stored-value argument the whole demolition was for. So `CREDIT_COPY_ALLOWED`
 * is gone with them: there is no list of words to protect from the deny-list.
 *
 * WHAT IS STILL NOT ON IT, AND THAT IS STILL DELIBERATE:
 *
 *   - **"send", "share" and "give".** They are the product's verbs for things
 *     that have nothing to do with money: you send a knock, you share a door, a
 *     recipient gives an answer. The constraint is about describing a PAID
 *     FEATURE that way, which is a claim about a sentence's subject and not
 *     about a token in it — a substring scan cannot tell the difference and
 *     would fail on correct copy every time. "transfer" is on the list because
 *     it is not a verb this product uses for anything else; what actually
 *     enforces non-transferability is still structural, and stronger than a
 *     word: an allowance holds nothing that could be moved, and there is no
 *     row anywhere that one account could move to another.
 *   - **bare "free".** The Free tier is a tier, a free door is a door, and
 *     "free trial" is a phrase three consumer-protection regimes require. The
 *     volume-pricing sense — "free credits" — is on the list as the phrase it
 *     is.
 *
 * "money" IS on the list now. It left the scan in 2.1 because "Money UX" and
 * "before money moves" are how this repository names its own consent rules —
 * but those are comments and headings in the source, and this list is matched
 * against RENDERED TEXT, which never contains either.
 */

/**
 * Words that may not appear in copy about a paid feature. Matched
 * case-insensitively, as whole words or phrases, against RENDERED text — what a
 * person reads, not what a component is called.
 */
export const MONEY_COPY_DENYLIST = [
  "gift",
  "gift card",
  "voucher",
  "credit",
  "credits",
  "balance",
  "wallet",
  "funds",
  "money",
  "cash",
  "bonus",
  "free credits",
  "extra credits",
  "transfer",
  /**
   * The internal word for usage past a line (ruling R-0923-3 (8)): the piece
   * of work that crossed the allowance, or the pay-as-you-go maximum, and
   * carries into the next window. It is the code's word and the vocabulary's,
   * never the subscriber's — a person reads "past this month's allowance".
   */
  "overdraw",
] as const;

/**
 * THE LABEL ON ANYTHING NOB WROTE FOR AN OWNER TO EDIT (EU AI Act Art. 50(2);
 * ruling R-0914-1 (11)).
 *
 * It lives beside the deny-list because it is the same kind of rule — a
 * sentence the product is obliged to say, in one wording, wherever the case
 * arises — and because the three surfaces that owe it are in two different
 * directories: the public-line draft on the door page, and the bio and rules
 * proposals in the owner's own thread. Two wordings for one obligation is one
 * wording too many, and the previous line ("Drafted. Edit it, then save.") said
 * what happened without ever saying who wrote it.
 */
export const AI_DRAFT_LABEL = "Written by Nob (AI) — edit it";
